import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'microsoft-outlook',
  version: '1.0.0',
  properties: [
    // === Authentication ===
    {
      key: 'useCredential',
      label: 'Use OAuth2 Credential',
      type: 'boolean',
      default: true,
      description: 'Use stored OAuth2 credential for user-level access to Outlook'
    },
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      required: true,
      typeOptions: {
        credentialType: 'microsoft_outlook_oauth',
      },
      displayOptions: {
        show: {
          useCredential: [true]
        }
      },
      description: 'Select Microsoft Outlook OAuth2 credential'
    },

    // === Resource Selection ===
    {
      key: 'resource',
      label: 'Resource',
      type: 'options',
      required: true,
      default: 'message',
      options: [
        { value: 'message', name: 'Message', description: 'Work with email messages' },
        { value: 'draft', name: 'Draft', description: 'Work with email drafts' },
        { value: 'folder', name: 'Folder', description: 'Work with mail folders' },
        { value: 'contact', name: 'Contact', description: 'Work with contacts' },
        { value: 'calendar', name: 'Calendar', description: 'Work with calendars' },
        { value: 'event', name: 'Event', description: 'Work with calendar events' },
        { value: 'attachment', name: 'Attachment', description: 'Work with message attachments' }
      ]
    },

    // === MESSAGE Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'send',
      displayOptions: {
        show: {
          resource: ['message']
        }
      },
      options: [
        { value: 'send', name: 'Send', description: 'Send an email message' },
        { value: 'reply', name: 'Reply', description: 'Reply to a message' },
        { value: 'get', name: 'Get', description: 'Get a single message' },
        { value: 'getAll', name: 'Get Many', description: 'Get multiple messages' },
        { value: 'move', name: 'Move', description: 'Move a message to a folder' },
        { value: 'update', name: 'Update', description: 'Update a message' },
        { value: 'delete', name: 'Delete', description: 'Delete a message' }
      ]
    },

    // === DRAFT Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'create',
      displayOptions: {
        show: {
          resource: ['draft']
        }
      },
      options: [
        { value: 'create', name: 'Create', description: 'Create a draft message' },
        { value: 'send', name: 'Send', description: 'Send an existing draft' },
        { value: 'get', name: 'Get', description: 'Get a draft' },
        { value: 'update', name: 'Update', description: 'Update a draft' },
        { value: 'delete', name: 'Delete', description: 'Delete a draft' }
      ]
    },

    // === FOLDER Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getAll',
      displayOptions: {
        show: {
          resource: ['folder']
        }
      },
      options: [
        { value: 'create', name: 'Create', description: 'Create a new mail folder' },
        { value: 'get', name: 'Get', description: 'Get a folder' },
        { value: 'getAll', name: 'Get Many', description: 'Get all folders' },
        { value: 'update', name: 'Update', description: 'Update a folder' },
        { value: 'delete', name: 'Delete', description: 'Delete a folder' },
        { value: 'getMessages', name: 'Get Messages', description: 'Get messages from a folder' }
      ]
    },

    // === CONTACT Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getAll',
      displayOptions: {
        show: {
          resource: ['contact']
        }
      },
      options: [
        { value: 'create', name: 'Create', description: 'Create a new contact' },
        { value: 'get', name: 'Get', description: 'Get a contact' },
        { value: 'getAll', name: 'Get Many', description: 'Get all contacts' },
        { value: 'update', name: 'Update', description: 'Update a contact' },
        { value: 'delete', name: 'Delete', description: 'Delete a contact' }
      ]
    },

    // === CALENDAR Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getAll',
      displayOptions: {
        show: {
          resource: ['calendar']
        }
      },
      options: [
        { value: 'create', name: 'Create', description: 'Create a new calendar' },
        { value: 'get', name: 'Get', description: 'Get a calendar' },
        { value: 'getAll', name: 'Get Many', description: 'Get all calendars' },
        { value: 'update', name: 'Update', description: 'Update a calendar' },
        { value: 'delete', name: 'Delete', description: 'Delete a calendar' }
      ]
    },

    // === EVENT Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'create',
      displayOptions: {
        show: {
          resource: ['event']
        }
      },
      options: [
        { value: 'create', name: 'Create', description: 'Create a calendar event' },
        { value: 'get', name: 'Get', description: 'Get an event' },
        { value: 'getAll', name: 'Get Many', description: 'Get all events' },
        { value: 'update', name: 'Update', description: 'Update an event' },
        { value: 'delete', name: 'Delete', description: 'Delete an event' }
      ]
    },

    // === ATTACHMENT Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getAll',
      displayOptions: {
        show: {
          resource: ['attachment']
        }
      },
      options: [
        { value: 'add', name: 'Add', description: 'Add an attachment to a message' },
        { value: 'get', name: 'Get', description: 'Get an attachment' },
        { value: 'getAll', name: 'Get Many', description: 'Get all attachments' },
        { value: 'download', name: 'Download', description: 'Download attachment content' }
      ]
    },

    // ===============================
    // MESSAGE PROPERTIES
    // ===============================

    // Send Message Properties
    {
      key: 'toRecipients',
      label: 'To',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['send']
        }
      },
      description: 'Comma-separated list of recipient email addresses'
    },
    {
      key: 'subject',
      label: 'Subject',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['send']
        }
      },
      description: 'Email subject'
    },
    {
      key: 'bodyContent',
      label: 'Message Body',
      type: 'text',
      required: true,
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['send']
        }
      },
      description: 'The email message content',
      typeOptions: {
        rows: 10,
      },
    },
    {
      key: 'bodyContentType',
      label: 'Body Content Type',
      type: 'options',
      default: 'html',
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['send']
        }
      },
      options: [
        { value: 'html', name: 'HTML' },
        { value: 'text', name: 'Plain Text' }
      ]
    },
    {
      key: 'ccRecipients',
      label: 'CC',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['send']
        }
      },
      description: 'Comma-separated list of CC email addresses'
    },
    {
      key: 'bccRecipients',
      label: 'BCC',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['send']
        }
      },
      description: 'Comma-separated list of BCC email addresses'
    },
    {
      key: 'importance',
      label: 'Importance',
      type: 'options',
      default: 'normal',
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['send']
        }
      },
      options: [
        { value: 'low', name: 'Low' },
        { value: 'normal', name: 'Normal' },
        { value: 'high', name: 'High' }
      ]
    },
    {
      key: 'saveToSentItems',
      label: 'Save to Sent Items',
      type: 'boolean',
      default: true,
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['send']
        }
      },
      description: 'Save a copy of the message in Sent Items folder'
    },

    // Attachment for Send
    {
      key: 'attachmentContent',
      label: 'Attachment',
      type: 'text',
      default: '',
      customComponent: 'FileUpload',
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['send']
        }
      },
      description: 'Upload a file to attach to the email. Use {{ }} expressions to reference upstream node output.'
    },
    {
      key: 'fileName',
      label: 'Attachment File Name',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['send']
        }
      },
      description: 'Name of the attachment file. Auto-populated from file upload, or use {{ }} expressions e.g. {{ $node["Download"].output.fileName }}'
    },
    {
      key: 'mimeType',
      label: 'Attachment MIME Type',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['send']
        }
      },
      description: 'MIME type of the attachment. Auto-populated from file upload, or use {{ }} expressions. Leave empty to auto-detect from file name.'
    },

    // Reply Properties
    {
      key: 'messageId',
      label: 'Message ID',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['reply', 'get', 'move', 'update', 'delete']
        }
      },
      description: 'The ID of the message'
    },
    {
      key: 'replyComment',
      label: 'Reply Message',
      type: 'text',
      required: true,
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['reply']
        }
      },
      description: 'The reply message content',
      typeOptions: {
        rows: 8,
      },
    },
    {
      key: 'replyAll',
      label: 'Reply All',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['reply']
        }
      },
      description: 'Reply to all recipients'
    },

    // Get Many Messages Properties
    {
      key: 'returnAll',
      label: 'Return All',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          resource: ['message', 'folder', 'contact', 'calendar', 'event', 'attachment'],
          operation: ['getAll', 'getMessages']
        }
      },
      description: 'Return all results or limit the number'
    },
    {
      key: 'limit',
      label: 'Limit',
      type: 'number',
      default: 50,
      displayOptions: {
        show: {
          resource: ['message', 'folder', 'contact', 'calendar', 'event', 'attachment'],
          operation: ['getAll', 'getMessages'],
          returnAll: [false]
        }
      },
      description: 'Maximum number of results to return'
    },

    // Message Filters
    {
      key: 'filterBySender',
      label: 'Filter by Sender',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['getAll']
        }
      },
      description: 'Filter messages by sender email address'
    },
    {
      key: 'filterByReadStatus',
      label: 'Read Status',
      type: 'options',
      default: 'all',
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['getAll']
        }
      },
      options: [
        { value: 'all', name: 'All' },
        { value: 'read', name: 'Read Only' },
        { value: 'unread', name: 'Unread Only' }
      ]
    },
    {
      key: 'filterHasAttachments',
      label: 'Has Attachments',
      type: 'options',
      default: 'all',
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['getAll']
        }
      },
      options: [
        { value: 'all', name: 'All' },
        { value: 'yes', name: 'Yes' },
        { value: 'no', name: 'No' }
      ]
    },
    {
      key: 'receivedAfter',
      label: 'Received After',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['getAll']
        }
      },
      description: 'Filter messages received after this date (ISO 8601 format, e.g., 2024-01-15T00:00:00Z)'
    },
    {
      key: 'receivedBefore',
      label: 'Received Before',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['getAll']
        }
      },
      description: 'Filter messages received before this date (ISO 8601 format)'
    },
    {
      key: 'downloadAttachments',
      label: 'Download Attachments',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['get', 'getAll']
        }
      },
      description: 'Download message attachments as binary data'
    },

    // Move Message
    {
      key: 'destinationFolderId',
      label: 'Destination Folder',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['move']
        }
      },
      description: 'The folder to move the message to',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFolders',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Update Message
    {
      key: 'updateIsRead',
      label: 'Mark as Read',
      type: 'options',
      default: 'noChange',
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['update']
        }
      },
      options: [
        { value: 'noChange', name: 'No Change' },
        { value: 'read', name: 'Mark as Read' },
        { value: 'unread', name: 'Mark as Unread' }
      ]
    },
    {
      key: 'updateCategories',
      label: 'Categories',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['message'],
          operation: ['update']
        }
      },
      description: 'Comma-separated list of categories to assign'
    },

    // ===============================
    // DRAFT PROPERTIES
    // ===============================
    {
      key: 'draftToRecipients',
      label: 'To',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['draft'],
          operation: ['create', 'update']
        }
      },
      description: 'Comma-separated list of recipient email addresses'
    },
    {
      key: 'draftSubject',
      label: 'Subject',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['draft'],
          operation: ['create', 'update']
        }
      },
      description: 'Draft subject'
    },
    {
      key: 'draftBodyContent',
      label: 'Body',
      type: 'text',
      default: '',
      displayOptions: {
        show: {
          resource: ['draft'],
          operation: ['create', 'update']
        }
      },
      description: 'Draft body content',
      typeOptions: {
        rows: 10,
      },
    },
    {
      key: 'draftBodyContentType',
      label: 'Body Content Type',
      type: 'options',
      default: 'html',
      displayOptions: {
        show: {
          resource: ['draft'],
          operation: ['create', 'update']
        }
      },
      options: [
        { value: 'html', name: 'HTML' },
        { value: 'text', name: 'Plain Text' }
      ]
    },
    {
      key: 'draftId',
      label: 'Draft ID',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['draft'],
          operation: ['send', 'get', 'update', 'delete']
        }
      },
      description: 'The ID of the draft'
    },

    // ===============================
    // FOLDER PROPERTIES
    // ===============================
    {
      key: 'folderId',
      label: 'Folder',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['folder'],
          operation: ['get', 'update', 'delete', 'getMessages']
        }
      },
      description: 'The mail folder to use',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFolders',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'folderName',
      label: 'Folder Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['folder'],
          operation: ['create']
        }
      },
      description: 'The name of the new folder'
    },
    {
      key: 'parentFolderId',
      label: 'Parent Folder',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['folder'],
          operation: ['create']
        }
      },
      description: 'The parent folder (leave empty for root)',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFolders',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'updateFolderName',
      label: 'New Folder Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['folder'],
          operation: ['update']
        }
      },
      description: 'The new name for the folder'
    },
    {
      key: 'includeChildFolders',
      label: 'Include Child Folders',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          resource: ['folder'],
          operation: ['getAll']
        }
      },
      description: 'Include nested child folders in the results'
    },

    // ===============================
    // CONTACT PROPERTIES
    // ===============================
    {
      key: 'contactId',
      label: 'Contact ID',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['contact'],
          operation: ['get', 'update', 'delete']
        }
      },
      description: 'The ID of the contact'
    },
    {
      key: 'contactGivenName',
      label: 'First Name',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['contact'],
          operation: ['create', 'update']
        }
      },
      description: 'Contact first name'
    },
    {
      key: 'contactSurname',
      label: 'Last Name',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['contact'],
          operation: ['create', 'update']
        }
      },
      description: 'Contact last name'
    },
    {
      key: 'contactEmailAddress',
      label: 'Email Address',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['contact'],
          operation: ['create', 'update']
        }
      },
      description: 'Contact email address'
    },
    {
      key: 'contactBusinessPhone',
      label: 'Business Phone',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['contact'],
          operation: ['create', 'update']
        }
      },
      description: 'Contact business phone number'
    },
    {
      key: 'contactMobilePhone',
      label: 'Mobile Phone',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['contact'],
          operation: ['create', 'update']
        }
      },
      description: 'Contact mobile phone number'
    },
    {
      key: 'contactCompanyName',
      label: 'Company Name',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['contact'],
          operation: ['create', 'update']
        }
      },
      description: 'Contact company name'
    },
    {
      key: 'contactJobTitle',
      label: 'Job Title',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['contact'],
          operation: ['create', 'update']
        }
      },
      description: 'Contact job title'
    },

    // ===============================
    // CALENDAR PROPERTIES
    // ===============================
    {
      key: 'calendarId',
      label: 'Calendar',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['calendar'],
          operation: ['get', 'update', 'delete']
        }
      },
      description: 'The calendar to use',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getCalendars',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'calendarName',
      label: 'Calendar Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['calendar'],
          operation: ['create']
        }
      },
      description: 'The name of the new calendar'
    },
    {
      key: 'updateCalendarName',
      label: 'New Calendar Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['calendar'],
          operation: ['update']
        }
      },
      description: 'The new name for the calendar'
    },

    // ===============================
    // EVENT PROPERTIES
    // ===============================
    {
      key: 'eventId',
      label: 'Event ID',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['get', 'update', 'delete']
        }
      },
      description: 'The ID of the event'
    },
    {
      key: 'eventCalendarId',
      label: 'Calendar',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['create', 'getAll']
        }
      },
      description: 'The calendar to use (leave empty for default calendar)',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getCalendars',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'eventSubject',
      label: 'Subject',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['create']
        }
      },
      description: 'Event subject/title'
    },
    {
      key: 'eventStartTime',
      label: 'Start Time',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['create']
        }
      },
      description: 'Event start time (ISO 8601 format, e.g., 2024-01-15T10:00:00)'
    },
    {
      key: 'eventEndTime',
      label: 'End Time',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['create']
        }
      },
      description: 'Event end time (ISO 8601 format, e.g., 2024-01-15T11:00:00)'
    },
    {
      key: 'eventTimeZone',
      label: 'Time Zone',
      type: 'string',
      default: 'UTC',
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['create']
        }
      },
      description: 'Time zone for the event (e.g., UTC, Pacific Standard Time)'
    },
    {
      key: 'eventAttendees',
      label: 'Attendees',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['create']
        }
      },
      description: 'Comma-separated list of attendee email addresses'
    },
    {
      key: 'eventLocation',
      label: 'Location',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['create']
        }
      },
      description: 'Event location'
    },
    {
      key: 'eventBody',
      label: 'Description',
      type: 'text',
      default: '',
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['create']
        }
      },
      description: 'Event description/body',
      typeOptions: {
        rows: 6,
      },
    },
    {
      key: 'eventIsOnlineMeeting',
      label: 'Online Meeting',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['create']
        }
      },
      description: 'Create as a Teams online meeting'
    },
    {
      key: 'eventIsAllDay',
      label: 'All Day Event',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['create']
        }
      },
      description: 'Mark as an all-day event'
    },

    // Event Update Properties
    {
      key: 'updateEventSubject',
      label: 'Subject',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['update']
        }
      },
      description: 'Updated event subject (leave empty to keep current)'
    },
    {
      key: 'updateEventStartTime',
      label: 'Start Time',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['update']
        }
      },
      description: 'Updated start time (leave empty to keep current)'
    },
    {
      key: 'updateEventEndTime',
      label: 'End Time',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['update']
        }
      },
      description: 'Updated end time (leave empty to keep current)'
    },
    {
      key: 'updateEventLocation',
      label: 'Location',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['update']
        }
      },
      description: 'Updated location (leave empty to keep current)'
    },

    // Event Filters
    {
      key: 'eventStartAfter',
      label: 'Start After',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['getAll']
        }
      },
      description: 'Filter events starting after this date (ISO 8601 format)'
    },
    {
      key: 'eventStartBefore',
      label: 'Start Before',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['event'],
          operation: ['getAll']
        }
      },
      description: 'Filter events starting before this date (ISO 8601 format)'
    },

    // ===============================
    // ATTACHMENT PROPERTIES
    // ===============================
    {
      key: 'attachmentMessageId',
      label: 'Message ID',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['attachment'],
          operation: ['add', 'get', 'getAll', 'download']
        }
      },
      description: 'The ID of the message'
    },
    {
      key: 'attachmentId',
      label: 'Attachment ID',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['attachment'],
          operation: ['get', 'download']
        }
      },
      description: 'The ID of the attachment'
    },
    {
      key: 'attachmentName',
      label: 'File Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['attachment'],
          operation: ['add']
        }
      },
      description: 'The name of the attachment file'
    },
    {
      key: 'attachmentContentType',
      label: 'Content Type',
      type: 'string',
      default: 'application/octet-stream',
      displayOptions: {
        show: {
          resource: ['attachment'],
          operation: ['add']
        }
      },
      description: 'MIME type of the attachment (e.g., application/pdf, image/png)'
    },
    {
      key: 'attachmentContentBase64',
      label: 'Content (Base64)',
      type: 'text',
      required: true,
      displayOptions: {
        show: {
          resource: ['attachment'],
          operation: ['add']
        }
      },
      description: 'Base64-encoded content of the attachment'
    },

    // ===============================
    // OUTPUT OPTIONS
    // ===============================
    {
      key: 'simplifyOutput',
      label: 'Simplify Output',
      type: 'boolean',
      default: true,
      displayOptions: {
        show: {
          resource: ['message', 'contact', 'event'],
          operation: ['get', 'getAll']
        }
      },
      description: 'Return a simplified version of the response'
    },

    // === ATTACHMENT DOWNLOAD OPTIONS ===
    {
      key: 'decodeTextContent',
      label: 'Decode Text Content',
      type: 'boolean',
      default: false,
      description: 'Automatically decode text attachments (CSV, JSON, TXT, XML) to readable text. When enabled, returns "content" as text in addition to contentBase64.',
      displayOptions: {
        show: {
          resource: ['attachment'],
          operation: ['download']
        }
      }
    },
    {
      key: 'parseJsonContent',
      label: 'Parse JSON Content',
      type: 'boolean',
      default: false,
      description: 'Automatically parse JSON attachments into an object/array. When enabled, returns "json" as the parsed data. This is faster than using a Code node for large JSON files.',
      displayOptions: {
        show: {
          resource: ['attachment'],
          operation: ['download'],
          decodeTextContent: [true]
        }
      }
    }
  ]
};

export default schema;
